import { ACHIEVEMENTS_BY_ID } from "../stats/achievements";
import { deckSku, hasHostComplimentaryAccess, ownsSku, type Sku } from "./entitlements";
import { levelProgress } from "../stats/levels";
import type { PlayerProfile } from "../stats/profile";
import { deckUnlockOf, SETS } from "./sets";
import { eventById, hasEventClosed } from "./events";
import { campaignVisible } from "../campaign/release";

/**
 * What's locked, and how you open it.
 *
 * Two tracks, deliberately not redundant:
 *   - LEVEL unlocks are generic: any achievement counts the same, so simply
 *     playing broadly opens things up.
 *   - ACHIEVEMENT unlocks are specific: a particular thing you did.
 *
 * Rule: nothing is gated behind a top-tier achievement. The grindy 500/1000-round
 * tiers exist for their own sake and never hold a cosmetic hostage.
 */
export type UnlockRule =
  | { kind: "starter" }
  | { kind: "level"; level: number }
  | { kind: "achievement"; achievementId: string }
  /**
   * Bought, not earned. The only rule here that does not read the profile:
   * levels and achievements are things this player did, and a purchase is a
   * thing they own, which is why it goes through entitlements.ts instead.
   *
   * Nothing behind a purchase may ever affect play. The catalog is decks,
   * venues and effects for exactly that reason.
   */
  | { kind: "purchase"; sku: Sku }
  /**
   * Given away for taking part in a seasonal event (theme/events.ts): a round
   * played signed in with Google while the window is open. Earned, so it is
   * handed over as a gift like a level or an achievement — but for a limited
   * time, which no other rule here has, and which is why it alone can take
   * something off the shelves (see `isOffered`).
   */
  | { kind: "event"; eventId: string }
  /**
   * Has played a round in this venue: what keeps a venue that used to be a
   * starter on the shelves of the players who were already using it, after it
   * became something to earn. Only ever used inside `anyOf`, after the rule
   * that is the real way to get it - which is the one a locked tile names.
   */
  | { kind: "venuePlayed"; venueId: string }
  /** Unlocked when any of these is; described (and gifted) as the first. */
  | { kind: "anyOf"; rules: UnlockRule[] };

export interface UnlockState {
  unlocked: boolean;
  /** Short human-readable requirement, e.g. "Reach level 4". Empty for starters. */
  requirement: string;
}

export function describeRule(rule: UnlockRule): string {
  switch (rule.kind) {
    case "starter":
      return "";
    case "level":
      return `Reach level ${rule.level}`;
    case "achievement": {
      const a = ACHIEVEMENTS_BY_ID.get(rule.achievementId);
      return a ? `Earn “${a.name}”` : "Earn a hidden achievement";
    }
    /*
     * NOT a requirement, because there is nothing the player can do. Every other
     * rule here describes an action ("Reach level 4"), and the carousel prints
     * whatever comes back in the same place — so this has to be honest about
     * being a wait rather than a task. It becomes a price when billing lands.
     */
    case "purchase":
      return "Coming soon";
    case "event": {
      const event = eventById(rule.eventId);
      if (!event) return "No longer available";
      return "month" in event
        ? `Play a round signed in with Google in ${event.monthName}`
        : `Play a round signed in with Google by ${event.lastDay}`;
    }
    case "venuePlayed":
      return "";
    case "anyOf":
      return rule.rules.length ? describeRule(rule.rules[0]) : "";
  }
}

/** True for a deck nobody can open by playing — see `describeRule`. */
export function isComingSoon(rule: UnlockRule | undefined): boolean {
  return rule?.kind === "purchase";
}

export function evaluateUnlock(rule: UnlockRule, profile: PlayerProfile): UnlockState {
  switch (rule.kind) {
    case "starter":
      return { unlocked: true, requirement: "" };
    case "level": {
      const { level } = levelProgress(profile);
      return { unlocked: level >= rule.level, requirement: describeRule(rule) };
    }
    case "achievement":
      return { unlocked: !!profile.achievements[rule.achievementId], requirement: describeRule(rule) };
    /*
     * Owned outright, OR comped because this account is an approved host.
     *
     * TODAY ONLY THE COMP EVER SAYS YES: `ownsSku` is false for everything until
     * there is a store, so being on the server's `allowedHosts` list is the one
     * and only way to hold a premium deck. That is a deliberate narrowing — the
     * `?entitle=` URL override that used to be the second way is gone.
     *
     * The two are still asked separately rather than folded into one owned-set:
     * a comp is a capability granted by an allow-list the player does not
     * control, and writing it in as ownership would make an admin removing
     * somebody from `allowedHosts` look identical to confiscating a deck they
     * had bought.
     */
    case "purchase":
      return { unlocked: ownsSku(rule.sku) || hasHostComplimentaryAccess(), requirement: describeRule(rule) };
    /*
     * The profile's record of having taken part, and nothing else — not the
     * calendar, so it never expires on the people who were there, and not the
     * host comp, because an event is something a player DID. A host who sat out
     * the window is in the same position as anybody else who did.
     */
    case "event":
      return { unlocked: !!profile.events[rule.eventId], requirement: describeRule(rule) };
    case "venuePlayed":
      return { unlocked: !!profile.stats.venuesPlayed[rule.venueId], requirement: describeRule(rule) };
    case "anyOf":
      return { unlocked: rule.rules.some((r) => evaluateUnlock(r, profile).unlocked), requirement: describeRule(rule) };
  }
}

/**
 * Whether something belongs on a shelf at all.
 *
 * True for everything but one case: an event's prize, while its window is
 * closed, for a player who never took part. Every other locked thing stays on
 * show because there is still a way to get it and seeing it is the invitation;
 * a closed event has no way today, and a tile saying so is a door painted on a
 * wall. A yearly event comes back with its month, and goes again when the month
 * ends. Those who did take part keep it on their shelves for good, and anybody
 * can still SEE it on a table whose host is using it, because that reads the
 * catalog directly rather than asking this.
 */
export function isOffered(rule: UnlockRule | undefined, profile: PlayerProfile, nowMs: number = Date.now()): boolean {
  if (rule?.kind !== "event") return true;
  return !!profile.events[rule.eventId] || !hasEventClosed(rule.eventId, nowMs);
}

/**
 * Venue unlocks. Coastal and Alpine are the starters; the other four mix the two
 * tracks so there's always something to chase from either direction.
 *
 * A venue that ships as half of a set is unlocked by that set's rule, which for
 * a sold set is the DECK's SKU — one purchase, two cosmetics, and no second SKU
 * to reconcile with the generated block below. See theme/sets.ts and
 * docs/PREMIUM_VENUES.md.
 */
export const VENUE_UNLOCKS: Record<string, UnlockRule> = {
  coastal: { kind: "starter" },
  // The campaign's second room: clearing chapter 2, which is played there,
  // opens it. Anybody who had already played a round in it keeps it, since it
  // was a starter until the campaign. Where the campaign is not offered
  // (production, for now - campaign/release.ts) it is still a starter, or it
  // could not be got at all.
  alpine: campaignVisible()
    ? { kind: "anyOf", rules: [{ kind: "achievement", achievementId: "campaign.chapter.2" }, { kind: "venuePlayed", venueId: "alpine" }] }
    : { kind: "starter" },
  // The campaign's third room, opened by clearing chapter 3 the same way, and
  // kept by anybody who has already played a round there. Hidden campaign:
  // still the level it always was.
  library: campaignVisible()
    ? { kind: "anyOf", rules: [{ kind: "achievement", achievementId: "campaign.chapter.3" }, { kind: "venuePlayed", venueId: "library" }] }
    : { kind: "level", level: 3 },
  // The campaign's fourth room, opened by clearing chapter 4 the same way, and
  // kept by anybody who has already played a round there. Hidden campaign:
  // still the first-solo-round unlock it always was.
  pavilion: campaignVisible()
    ? { kind: "anyOf", rules: [{ kind: "achievement", achievementId: "campaign.chapter.4" }, { kind: "venuePlayed", venueId: "pavilion" }] }
    : { kind: "achievement", achievementId: "solo.complete.1" },
  // The campaign's fifth room, opened by clearing chapter 5 the same way, and
  // kept by anybody who has already played a round there. Hidden campaign:
  // still the level it always was.
  observatory: campaignVisible()
    ? { kind: "anyOf", rules: [{ kind: "achievement", achievementId: "campaign.chapter.5" }, { kind: "venuePlayed", venueId: "observatory" }] }
    : { kind: "level", level: 7 },
  velvet: { kind: "achievement", achievementId: "match.streak.3" },
  ...Object.fromEntries(SETS.map((s) => [s.venueId, s.unlock])),
};

/**
 * A painted deck given away on its own, with no room to make it a set. Spread in
 * after the generated block, for the reason the sets are.
 */
const GIVEN_DECKS: Record<string, UnlockRule> = {
  "yuletide-joy": { kind: "event", eventId: "yuletide" },
};

/**
 * Card deck unlocks. Two starters, the rest split between levels and specific
 * achievements — e.g. Ember is for finishing a 2-deck solo round, which is the
 * long strategic haul rather than a grind.
 */
export const DECK_UNLOCKS: Record<string, UnlockRule> = {
  "classic-slate": { kind: "starter" },
  forest: { kind: "starter" },
  harbor: { kind: "level", level: 2 },
  plum: { kind: "achievement", achievementId: "fiveout.total.10" },
  clay: { kind: "level", level: 5 },
  ember: { kind: "achievement", achievementId: "solo.complete.2" },
  frost: { kind: "level", level: 9 },
  // "Surgical — Full Deck": a solo full deck in 55 turns or fewer. The one
  // skill-only unlock - no opponent to get lucky against, only Match guesses
  // and remembering taken piles. It was Chain of 5 until the chain tiers were
  // cut to 2 and 3 as luck rather than play, which left Orchid unobtainable
  // (unlocks.test.ts now catches that).
  orchid: { kind: "achievement", achievementId: "solo.turns.1.55" },
  // Premium decks, generated alongside their CARD_THEMES entries — see
  // scripts/lib/deckgen/install.mjs.
  // >>> generated by scripts/deckgen.mjs - do not edit by hand
  "animal-antics-deck": { kind: "purchase", sku: deckSku("animal-antics-deck") },
  "biolume-deep": { kind: "purchase", sku: deckSku("biolume-deep") },
  "fractal-aether": { kind: "purchase", sku: deckSku("fractal-aether") },
  "midnight-macabre": { kind: "purchase", sku: deckSku("midnight-macabre") },
  "mythic-courts": { kind: "purchase", sku: deckSku("mythic-courts") },
  "norse-realms": { kind: "purchase", sku: deckSku("norse-realms") },
  "olympian-pantheon": { kind: "purchase", sku: deckSku("olympian-pantheon") },
  "world-in-bloom": { kind: "purchase", sku: deckSku("world-in-bloom") },
  "yuletide-joy": { kind: "purchase", sku: deckSku("yuletide-joy") },
  // <<< end generated
  /*
   * A set that is given away rather than sold takes its deck with it. The
   * generator registers every painted deck as a purchase, because that is what
   * a painted deck is by default; this is where a set says otherwise, AFTER the
   * generated block so it wins, and without editing generated code. A set whose
   * deck is earned on its own (Fractal Aether's 15 Jackpots) names that rule as
   * `deckUnlock`, and it wins the same way.
   */
  ...Object.fromEntries(
    SETS.filter((s) => s.deckUnlock || s.unlock.kind !== "purchase").map((s) => [s.deckId, deckUnlockOf(s)]),
  ),
  ...GIVEN_DECKS,
};
